import { useRef, useState } from 'react'
import { Dimensions, ScrollView } from 'react-native'
import { useFocusEffect } from '@react-navigation/native'
import { useCallback } from 'react'
import { FullRegistrationData, useCompleteOnboarding } from '@hooks'
import { ScreenAuthProps } from '@navigation'
import { secureTokenStore, useAuthCredentialsStore } from '@store'
import { showApiErrorToast } from '@utils'
import { useLanguage } from '@context'
import dayjs from 'dayjs'
import { api } from '@api'

interface useRegistrationResult {
  scrollViewRef: React.RefObject<ScrollView>
  isPending: boolean
  currentStep: number
  handleNext: (newData: Partial<FullRegistrationData>) => void
  handleBack: () => void
}

const screenWidth = Dimensions.get('window').width

export const useRegistrationSteps = (
  navigation: ScreenAuthProps<'RegistrationSteps'>['navigation']
): useRegistrationResult => {
  const { t, currentLanguage } = useLanguage()
  const { mutateAsync: completeOnboarding, isPending: isPendingCompleteOnboarding } =
    useCompleteOnboarding()

  const { email } = useAuthCredentialsStore()
  const [isPendingRefresh, setIsPendingRefresh] = useState(false)

  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<FullRegistrationData>({} as FullRegistrationData)

  const scrollViewRef = useRef<ScrollView>(null)
  // A second press on the last "Suivant" while the profile is being created
  // must not send the profile again (the server would answer 409).
  const isSubmittingRef = useRef(false)

  useFocusEffect(
    useCallback(() => {
      setCurrentStep(0)
      setFormData({} as FullRegistrationData)
      scrollViewRef.current?.scrollTo({ x: 0, animated: false })
    }, [])
  )

  const handleNext = async (newData: Partial<FullRegistrationData>) => {
    const updatedFormData = { ...formData, ...newData }
    setFormData(updatedFormData)
    if (currentStep < 2) {
      scrollViewRef.current?.scrollTo({ x: screenWidth * (currentStep + 1), animated: true })
      setCurrentStep(currentStep + 1)
    } else {
      const token = secureTokenStore.getAccessToken()
      if (token) {
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        try {
          // The position is not asked here: the next screen has a box for it.
          await completeOnboarding({
            onboardingParams: {
              avatar: updatedFormData.avatar,
              gender: updatedFormData.gender,
              firstName: updatedFormData.firstName,
              lastName: updatedFormData.lastName,
              // The day as typed: a local midnight sent as an instant is the evening
              // before for the server, east of Greenwich.
              birthDate: dayjs(updatedFormData.birthday, 'DD-MM-YYYY').format('YYYY-MM-DD'),
              residence: updatedFormData.residence?.id,
              origins: (updatedFormData.origins || []).map(origin => origin.id),
              nationalities: updatedFormData.nationalities.map(nat => nat.id),
              languages: updatedFormData.languages.map(lang => ({ code: lang.id, level: 'fluent' })),
              bio: updatedFormData.info,
              contacts: { email },
              hashtags: updatedFormData.hashtags,
              moments: updatedFormData.moments.map(moment => moment.id),
              language: currentLanguage,
            },
            token,
          })

          const refreshToken = secureTokenStore.getRefreshToken()
          if (refreshToken) {
            setIsPendingRefresh(true)
            try {
              const { data } = await api.post('/authentication/refresh-token', { refreshToken })
              await secureTokenStore.setTokens(data.accessToken, data.refreshToken)
            } catch {
              // Non-fatal
            }
            setIsPendingRefresh(false)
          }

          navigation.navigate('FinalStep')
        } catch (err) {
          showApiErrorToast(t, err)
        } finally {
          isSubmittingRef.current = false
        }
      }
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      scrollViewRef.current?.scrollTo({ x: screenWidth * (currentStep - 1), animated: true })
    } else {
      navigation.goBack()
    }
  }
  return {
    scrollViewRef,
    isPending: isPendingCompleteOnboarding || isPendingRefresh,
    currentStep,
    handleNext,
    handleBack,
  }
}
